import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import dayjs from 'dayjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler);

const categories = ['Leads', 'Opportunities', 'Revenue', 'Team Performance'];
const salesPeople = ['all', 'Ava Wilson', 'Noah Bennett', 'Emma Reed', 'Liam Carter', 'Olivia Brooks'];
const teams = ['all', 'North Team', 'Enterprise Team', 'Growth Team', 'Strategic Team'];
const regions = ['all', 'North America', 'Europe', 'Middle East', 'APAC'];

const baseKpiByCategory = {
  Leads: { total: 428, conversion: '32%', growth: '+8.2%' },
  Opportunities: { total: 186, conversion: '41%', growth: '+6.4%' },
  Revenue: { total: 2140000, conversion: '58%', growth: '+12.1%' },
  'Team Performance': { total: 94, conversion: '71%', growth: '+5.6%' },
};

const getFilteredScale = filters => {
  let scale = 1;
  if (filters.salesPerson !== 'all') scale *= 0.74;
  if (filters.team !== 'all') scale *= 0.82;
  if (filters.region !== 'all') scale *= 0.79;
  const dateFrom = dayjs(filters.fromDate);
  const dateTo = dayjs(filters.toDate);
  const daySpan = Math.max(1, dateTo.diff(dateFrom, 'day') + 1);
  const dateRatio = Math.min(1, daySpan / 90);
  return Number((scale * dateRatio).toFixed(2));
};

export const Reports = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';

  const pageBg = isDark
    ? 'radial-gradient(circle at 10% 10%, #1e293b 0%, #0b1220 45%, #060b16 100%)'
    : 'linear-gradient(180deg, #f8fbff 0%, #f3f7fc 65%, #eef4fb 100%)';
  const shellBg = isDark ? 'rgba(15, 23, 42, 0.92)' : '#ffffff';
  const shellBorder = isDark ? '1px solid rgba(148, 163, 184, 0.22)' : '1px solid #dbe7f5';
  const panelBg = isDark ? 'rgba(30, 41, 59, 0.62)' : '#f8fafc';
  const panelBorder = isDark ? '1px solid rgba(148, 163, 184, 0.24)' : '1px solid #e2e8f0';
  const textPrimary = isDark ? '#e2e8f0' : '#0f172a';
  const textMuted = isDark ? '#94a3b8' : '#64748b';

  const [category, setCategory] = useState('Leads');
  const [filters, setFilters] = useState({
    fromDate: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    toDate: dayjs().format('YYYY-MM-DD'),
    salesPerson: 'all',
    team: 'all',
    region: 'all',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const scale = useMemo(() => getFilteredScale(filters), [filters]);

  const kpi = useMemo(() => {
    const base = baseKpiByCategory[category];
    return {
      total: Math.round(base.total * scale),
      conversion: `${Math.max(1, Math.round(parseInt(base.conversion, 10) * scale))}%`,
      growth: `${scale >= 0.9 ? '+' : ''}${(Number(base.growth.replace('%', '')) * scale).toFixed(1)}%`,
    };
  }, [category, scale]);

  const funnelData = useMemo(() => {
    const source = {
      Leads: [380, 260, 170, 120, 78],
      Opportunities: [210, 168, 134, 92, 64],
      Revenue: [180, 142, 115, 88, 70],
      'Team Performance': [220, 190, 156, 118, 90],
    };
    return source[category].map(value => Math.round(value * scale));
  }, [category, scale]);

  const barData = useMemo(() => {
    const source = {
      Leads: [120, 98, 84, 73, 65],
      Opportunities: [90, 82, 76, 70, 61],
      Revenue: [420, 390, 360, 330, 298],
      'Team Performance': [88, 84, 80, 78, 74],
    };
    return source[category].map(value => Math.round(value * scale));
  }, [category, scale]);

  const pieData = useMemo(() => {
    const source = {
      Leads: [42, 25, 18, 15],
      Opportunities: [35, 30, 20, 15],
      Revenue: [48, 26, 14, 12],
      'Team Performance': [30, 28, 24, 18],
    };
    return source[category].map(value => Math.max(1, Math.round(value * scale)));
  }, [category, scale]);

  const lineData = useMemo(() => {
    const source = {
      Leads: [54, 62, 71, 69, 84, 93],
      Opportunities: [28, 36, 44, 48, 57, 63],
      Revenue: [220, 248, 272, 301, 334, 368],
      'Team Performance': [61, 66, 70, 72, 77, 81],
    };
    return source[category].map(value => Math.round(value * scale));
  }, [category, scale]);

  const handleExport = type => {
    setSnackbar({
      open: true,
      message: `${type} export generated for ${category} report`,
      severity: 'success',
    });
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        minHeight: '100vh',
        bgcolor: isDark ? '#060b16' : '#f4f8ff',
        backgroundImage: pageBg,
        fontFamily: 'DM Sans, Segoe UI, sans-serif',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2.5, md: 3 },
          borderRadius: 3,
          border: shellBorder,
          background: shellBg,
          boxShadow: isDark
            ? '0 10px 34px rgba(2, 6, 23, 0.55)'
            : '0 12px 36px rgba(15, 23, 42, 0.08)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                background: isDark ? 'rgba(22, 163, 74, 0.2)' : '#ecfdf3',
                border: isDark ? '1px solid rgba(74, 222, 128, 0.35)' : '1px solid #bbf7d0',
              }}
            >
              <AssessmentIcon sx={{ color: '#22c55e', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: textPrimary, letterSpacing: 0.1 }}>
                CRM Reports
              </Typography>
              <Typography variant="body2" sx={{ color: textMuted }}>
                Executive analytics across leads, opportunities, revenue, and team effectiveness
              </Typography>
            </Box>
          </Box>

          <Stack direction={isMobile ? 'column' : 'row'} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<InsertDriveFileOutlinedIcon />}
              onClick={() => handleExport('CSV')}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderColor: isDark ? 'rgba(148, 163, 184, 0.45)' : '#cbd5e1',
                color: textPrimary,
                background: isDark ? 'rgba(15, 23, 42, 0.55)' : '#fff',
                '&:hover': {
                  borderColor: '#38bdf8',
                  background: isDark ? 'rgba(2, 132, 199, 0.18)' : '#f0f9ff',
                },
              }}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdfOutlinedIcon />}
              onClick={() => handleExport('PDF')}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                boxShadow: '0 8px 18px rgba(22, 163, 74, 0.28)',
                '&:hover': { background: 'linear-gradient(135deg, #15803d 0%, #166534 100%)' },
              }}
            >
              Export PDF
            </Button>
          </Stack>
        </Box>

        <Paper sx={{ border: panelBorder, borderRadius: 2.5, mb: 2, bgcolor: panelBg }}>
          <Tabs
            value={category}
            onChange={(_, value) => setCategory(value)}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{ p: 1, '& .MuiTabs-flexContainer': { gap: 0.8 } }}
          >
            {categories.map(item => (
              <Tab
                key={item}
                value={item}
                label={item}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 700,
                  color: textMuted,
                  '&.Mui-selected': {
                    bgcolor: isDark ? 'rgba(22, 163, 74, 0.24)' : '#ecfdf3',
                    color: isDark ? '#86efac' : '#166534',
                  },
                }}
              />
            ))}
          </Tabs>
        </Paper>

        <Paper sx={{ p: 1.5, borderRadius: 2.5, border: panelBorder, bgcolor: panelBg, mb: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr 1fr 1fr 1fr' },
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <TextField
                size="small"
                type="date"
                label="From"
                value={filters.fromDate}
                onChange={event => setFilters(prev => ({ ...prev, fromDate: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ bgcolor: isDark ? 'rgba(15, 23, 42, 0.65)' : '#fff' }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={filters.toDate}
                onChange={event => setFilters(prev => ({ ...prev, toDate: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ bgcolor: isDark ? 'rgba(15, 23, 42, 0.65)' : '#fff' }}
              />
            </Box>

            <FormControl size="small" sx={{ bgcolor: isDark ? 'rgba(15, 23, 42, 0.65)' : '#fff' }}>
              <InputLabel>Sales Person</InputLabel>
              <Select
                value={filters.salesPerson}
                label="Sales Person"
                onChange={event => setFilters(prev => ({ ...prev, salesPerson: event.target.value }))}
              >
                {salesPeople.map(item => (
                  <MenuItem key={item} value={item}>{item === 'all' ? 'All Sales People' : item}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ bgcolor: isDark ? 'rgba(15, 23, 42, 0.65)' : '#fff' }}>
              <InputLabel>Team</InputLabel>
              <Select
                value={filters.team}
                label="Team"
                onChange={event => setFilters(prev => ({ ...prev, team: event.target.value }))}
              >
                {teams.map(item => (
                  <MenuItem key={item} value={item}>{item === 'all' ? 'All Teams' : item}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ bgcolor: isDark ? 'rgba(15, 23, 42, 0.65)' : '#fff' }}>
              <InputLabel>Region</InputLabel>
              <Select
                value={filters.region}
                label="Region"
                onChange={event => setFilters(prev => ({ ...prev, region: event.target.value }))}
              >
                {regions.map(item => (
                  <MenuItem key={item} value={item}>{item === 'all' ? 'All Regions' : item}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Chip
              icon={<DownloadOutlinedIcon />}
              label={`${category} report`}
              sx={{
                justifySelf: { xs: 'start', md: 'end' },
                fontWeight: 700,
                bgcolor: isDark ? 'rgba(22, 163, 74, 0.22)' : '#ecfdf3',
                color: isDark ? '#86efac' : '#166534',
                border: isDark ? '1px solid rgba(74, 222, 128, 0.35)' : '1px solid #bbf7d0',
              }}
            />
          </Box>
        </Paper>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1, mb: 2 }}>
          <Paper sx={{ p: 1.5, borderRadius: 2.5, borderLeft: '4px solid #166534', background: panelBg, border: panelBorder }}>
            <Typography variant="caption" sx={{ color: textMuted }}>Total</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {category === 'Revenue' ? `$${kpi.total.toLocaleString()}` : kpi.total.toLocaleString()}
            </Typography>
          </Paper>
          <Paper sx={{ p: 1.5, borderRadius: 2.5, borderLeft: '4px solid #2563eb', background: panelBg, border: panelBorder }}>
            <Typography variant="caption" sx={{ color: textMuted }}>Conversion</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>{kpi.conversion}</Typography>
          </Paper>
          <Paper sx={{ p: 1.5, borderRadius: 2.5, borderLeft: '4px solid #f59e0b', background: panelBg, border: panelBorder }}>
            <Typography variant="caption" sx={{ color: textMuted }}>Growth</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>{kpi.growth}</Typography>
          </Paper>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
          <Paper sx={{ p: 1.5, borderRadius: 2.5, border: panelBorder, background: panelBg }}>
            <Typography sx={{ fontWeight: 800, mb: 1, color: textPrimary }}>Funnel Chart</Typography>
            <Bar
              data={{
                labels: ['Prospects', 'Qualified', 'Proposal', 'Negotiation', 'Closed'],
                datasets: [
                  {
                    label: 'Volume',
                    data: funnelData,
                    backgroundColor: ['#dcfce7', '#bbf7d0', '#86efac', '#4ade80', '#22c55e'],
                    borderColor: '#16a34a',
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                responsive: true,
                plugins: { legend: { display: false } },
                scales: {
                  x: { ticks: { color: textMuted }, grid: { color: isDark ? 'rgba(148,163,184,0.15)' : '#edf2f7' } },
                  y: { beginAtZero: true, ticks: { color: textMuted }, grid: { color: isDark ? 'rgba(148,163,184,0.15)' : '#edf2f7' } },
                },
              }}
            />
          </Paper>

          <Paper sx={{ p: 1.5, borderRadius: 2.5, border: panelBorder, background: panelBg }}>
            <Typography sx={{ fontWeight: 800, mb: 1, color: textPrimary }}>Bar Chart</Typography>
            <Bar
              data={{
                labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5'],
                datasets: [
                  {
                    label: category,
                    data: barData,
                    backgroundColor: '#60a5fa',
                    borderRadius: 6,
                  },
                ],
              }}
              options={{
                responsive: true,
                plugins: { legend: { display: false } },
                scales: {
                  x: { ticks: { color: textMuted }, grid: { color: isDark ? 'rgba(148,163,184,0.15)' : '#edf2f7' } },
                  y: { beginAtZero: true, ticks: { color: textMuted }, grid: { color: isDark ? 'rgba(148,163,184,0.15)' : '#edf2f7' } },
                },
              }}
            />
          </Paper>

          <Paper sx={{ p: 1.5, borderRadius: 2.5, border: panelBorder, background: panelBg }}>
            <Typography sx={{ fontWeight: 800, mb: 1, color: textPrimary }}>Pie Chart</Typography>
            <Box sx={{ maxWidth: 420, mx: 'auto' }}>
              <Pie
                data={{
                  labels: ['North', 'South', 'West', 'East'],
                  datasets: [
                    {
                      data: pieData,
                      backgroundColor: ['#34d399', '#60a5fa', '#f59e0b', '#a78bfa'],
                      borderWidth: 1,
                    },
                  ],
                }}
                options={{ responsive: true, plugins: { legend: { position: 'bottom', labels: { color: textMuted } } } }}
              />
            </Box>
          </Paper>

          <Paper sx={{ p: 1.5, borderRadius: 2.5, border: panelBorder, background: panelBg }}>
            <Typography sx={{ fontWeight: 800, mb: 1, color: textPrimary }}>Line Chart</Typography>
            <Line
              data={{
                labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
                datasets: [
                  {
                    label: 'Trend',
                    data: lineData,
                    borderColor: '#16a34a',
                    backgroundColor: 'rgba(22, 163, 74, 0.15)',
                    tension: 0.3,
                    fill: true,
                  },
                ],
              }}
              options={{
                responsive: true,
                plugins: { legend: { display: false } },
                scales: {
                  x: { ticks: { color: textMuted }, grid: { color: isDark ? 'rgba(148,163,184,0.15)' : '#edf2f7' } },
                  y: { ticks: { color: textMuted }, grid: { color: isDark ? 'rgba(148,163,184,0.15)' : '#edf2f7' } },
                },
              }}
            />
          </Paper>
        </Box>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={2200} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Reports;
